import type {
  AchievementType,
  ApiAchievement,
  ApiBanner,
  ApiBlogPost,
  ApiTestimonial,
  BannerPlacement,
} from '../api/types'
import { HERO_BANNER_ICONS, type IconName } from '../constants/icons'
import { normalizeAchievementType } from './achievements'

export type BannerCard = {
  id: string
  title: string
  subtitle: string | null
  icon: IconName
  placement: BannerPlacement
  imageUrl: string | null
  mobileImageUrl: string | null
  ctaLabel: string | null
  ctaUrl: string | null
  ctaIsExternal: boolean
}

export type HeroSlide = {
  id: string
  title: string
  imageUrl: string
  mobileImageUrl: string | null
  ctaUrl: string | null
  ctaIsExternal: boolean
}

export type PartnerLogo = {
  id: string
  title: string
  achievementType: AchievementType
  imageUrl: string
  linkUrl: string | null
}

export type TestimonialCard = {
  id: string
  customerName: string
  customerLocation: string | null
  quote: string
  rating: number | null
  avatarUrl: string | null
}

export type BlogPostCard = {
  id: string
  title: string
  slug: string
  excerpt: string | null
  coverImageUrl: string | null
  authorName: string
  publishedAt: string | null
  to: string
}

export type BlogPostDetail = BlogPostCard & {
  body: string
  tags: string[]
}

export function mapBannerCard(banner: ApiBanner, index = 0): BannerCard {
  return {
    id: banner.id,
    title: banner.title,
    subtitle: banner.subtitle,
    icon: HERO_BANNER_ICONS[index % HERO_BANNER_ICONS.length],
    placement: banner.placement,
    imageUrl: banner.imageUrl,
    mobileImageUrl: banner.mobileImageUrl,
    ctaLabel: banner.ctaLabel,
    ctaUrl: banner.ctaUrl,
    ctaIsExternal: isExternalUrl(banner.ctaUrl),
  }
}

export function mapHeroSlides(banners: BannerCard[]): HeroSlide[] {
  return banners
    .map((banner) => {
      const imageUrl = banner.imageUrl ?? banner.mobileImageUrl
      if (!imageUrl) return null
      return {
        id: banner.id,
        title: banner.title,
        imageUrl,
        mobileImageUrl: banner.mobileImageUrl,
        ctaUrl: banner.ctaUrl,
        ctaIsExternal: banner.ctaIsExternal,
      } satisfies HeroSlide
    })
    .filter((slide): slide is HeroSlide => slide != null)
}

export function mapPartnerLogos(achievements: ApiAchievement[]): PartnerLogo[] {
  return achievements
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title))
    .filter((item): item is ApiAchievement & { imageUrl: string } => Boolean(item.imageUrl))
    .map((item) => ({
      id: item.id,
      title: item.title,
      achievementType: normalizeAchievementType(item.achievementType),
      imageUrl: item.imageUrl,
      linkUrl: item.linkUrl,
    }))
}

export function mapTestimonialCard(testimonial: ApiTestimonial): TestimonialCard {
  return {
    id: testimonial.id,
    customerName: testimonial.customerName,
    customerLocation: testimonial.customerLocation,
    quote: testimonial.quote,
    rating: testimonial.rating,
    avatarUrl: testimonial.avatarUrl,
  }
}

export function mapBlogPostCard(post: ApiBlogPost): BlogPostCard {
  return {
    id: post.id,
    title: post.title,
    slug: post.slug,
    excerpt: post.excerpt,
    coverImageUrl: post.coverImageUrl,
    authorName: post.authorName,
    publishedAt: post.publishedAt,
    to: `/blog/${post.slug}`,
  }
}

export function mapBlogPostDetail(post: ApiBlogPost): BlogPostDetail {
  return {
    ...mapBlogPostCard(post),
    body: post.body,
    tags: post.tags ?? [],
  }
}

export function sortBySortOrder<T extends { sortOrder: number }>(rows: T[]): T[] {
  return rows.slice().sort((a, b) => a.sortOrder - b.sortOrder)
}

export function sortBlogPostsByPublishedAt(posts: ApiBlogPost[]): ApiBlogPost[] {
  return posts.slice().sort((a, b) => {
    const aTime = a.publishedAt ? Date.parse(a.publishedAt) : 0
    const bTime = b.publishedAt ? Date.parse(b.publishedAt) : 0
    return bTime - aTime
  })
}

function isExternalUrl(url: string | null): boolean {
  return Boolean(url && /^https?:\/\//i.test(url))
}
